import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "./supabase";
import type { Session } from "@supabase/supabase-js";

export interface PortailUser {
  id: string;
  resellerId: string;
  email: string;
  role: "admin" | "contact";
  prenom: string | null;
  nom: string | null;
  telephone: string | null;
  roleSociete: string | null;
  avatarUrl: string | null;
  actif: boolean;
}

export interface ResellerBrand {
  id: string;
  companyName: string;
  logoUrl: string | null;
  couleurPrimaire: string;
  couleurSecondaire: string;
}

interface AuthState {
  session: Session | null;
  portailUser: PortailUser | null;
  brand: ResellerBrand | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  refreshBrand: (partial: Partial<ResellerBrand>) => void;
}

const AuthContext = createContext<AuthState>({
  session: null,
  portailUser: null,
  brand: null,
  isLoading: true,
  isAuthenticated: false,
  logout: async () => {},
  refreshBrand: () => {},
});

export function PortailAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [portailUser, setPortailUser] = useState<PortailUser | null>(null);
  const [brand, setBrand] = useState<ResellerBrand | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPortailMe = async (accessToken: string): Promise<{ user: PortailUser; brand: ResellerBrand } | null> => {
    try {
      const res = await fetch("/api/portail/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        console.warn("[portail-auth] /api/portail/me returned", res.status);
        return null;
      }
      const data = await res.json();
      console.log("[portail-auth] user=", data.user?.email, "role=", data.user?.role, "revendeur=", data.brand?.companyName);
      return data as { user: PortailUser; brand: ResellerBrand };
    } catch (err) {
      console.error("[portail-auth] fetchPortailMe error:", err);
      return null;
    }
  };

  useEffect(() => {
    // Recovery routes must be allowed to consume Supabase's URL fragment before
    // the portal membership check can sign the user out. Normal validation
    // resumes after the recovery page navigates away.
    const isPasswordLifecyclePath = () => (
      window.location.pathname === "/auth/callback" ||
      window.location.pathname === "/reset-password"
    );

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      if (s?.access_token && !isPasswordLifecyclePath()) {
        const data = await fetchPortailMe(s.access_token);
        if (data) {
          setPortailUser(data.user);
          setBrand(data.brand);
        } else {
          await supabase.auth.signOut();
          setSession(null);
        }
      }
      setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, s) => {
      console.log("[portail-auth] onAuthStateChange event=", event);
      setSession(s);
      if (s?.access_token && !isPasswordLifecyclePath()) {
        const data = await fetchPortailMe(s.access_token);
        if (data) {
          setPortailUser(data.user);
          setBrand(data.brand);
        } else {
          setPortailUser(null);
          setBrand(null);
          await supabase.auth.signOut();
        }
      } else {
        setPortailUser(null);
        setBrand(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    setPortailUser(null);
    setBrand(null);
    setSession(null);
  };

  const refreshBrand = (partial: Partial<ResellerBrand>) => {
    setBrand(prev => prev ? { ...prev, ...partial } : prev);
  };

  return (
    <AuthContext.Provider value={{
      session,
      portailUser,
      brand,
      isLoading,
      isAuthenticated: !!session && !!portailUser,
      logout,
      refreshBrand,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function usePortailAuth() {
  return useContext(AuthContext);
}
